import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Phone, Mail, MapPin, UserCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default site config
const defaultConfig = {
  phone: '+34 661 19 40 23',
  contact_email: 'secretaria@aravellgolfclub.com',
  address: 'Ctra. de Tor s/n, 25598 Aravell, Lleida',
  maps_url: 'https://maps.google.com/?q=Aravell+Golf+Club',
  instagram_url: 'https://www.instagram.com/UGOLFARAVELLANDORRA',
  facebook_url: 'https://facebook.com/aravellgolf',
  linkedin_url: 'https://linkedin.com/company/aravellgolf'
};

// Default footer content
const defaultFooterContent = {
  leclub_url: 'https://leclub-golf.com/es',
  leclub_text: { ca: 'Membre de la xarxa LeClub Golf', es: 'Miembro de la red LeClub Golf', fr: 'Membre du réseau LeClub Golf', en: 'Member of LeClub Golf network' },
  nav_title: { ca: 'Navegació', es: 'Navegación', fr: 'Navigation', en: 'Navigation' },
  legal_title: { ca: 'Legal', es: 'Legal', fr: 'Légal', en: 'Legal' },
  social_title: { ca: 'Segueix-nos', es: 'Síguenos', fr: 'Suivez-nous', en: 'Follow us' },
  federations_title: { ca: 'Federacions', es: 'Federaciones', fr: 'Fédérations', en: 'Federations' },
  communities_title: { ca: 'Comunitats', es: 'Comunidades', fr: 'Communautés', en: 'Communities' }
};

// Default communities
const defaultCommunities = [
  { id: 'comm_nuts', name: 'Nuts Andorra', url: '/programa-nuts' },
  { id: 'comm_anyos', name: 'Anyós Park El Club', url: '/anyos-park' },
  { id: 'comm_andorra_connect', name: 'Andorra Connect', url: '/m/andorraconnect' }
];

// Default federations
const defaultFederations = [
  { id: 'fed_fag', name: 'Federació Andorrana de Golf', abbr: 'FAG', url: 'https://www.fagolf.com/' },
  { id: 'fed_fcg', name: 'Federació Catalana de Golf', abbr: 'FCG', url: 'https://www.catgolf.cat/' },
  { id: 'fed_rfeg', name: 'Real Federación Española de Golf', abbr: 'RFEG', url: 'https://www.rfegolf.es/' }
];

export const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();
  
  const [siteConfig, setSiteConfig] = useState(defaultConfig);
  const [footerContent, setFooterContent] = useState(defaultFooterContent);
  const [federations, setFederations] = useState(defaultFederations);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [configRes, footerRes, fedRes] = await Promise.all([
          fetch(`${API_URL}/api/public/config`).catch(() => null),
          fetch(`${API_URL}/api/footer-content`).catch(() => null),
          fetch(`${API_URL}/api/federations`).catch(() => null)
        ]);

        if (configRes && configRes.ok) {
          const config = await configRes.json();
          setSiteConfig(prev => ({ ...prev, ...config }));
        }

        if (footerRes && footerRes.ok) {
          const content = await footerRes.json();
          setFooterContent(prev => ({ ...prev, ...content }));
        }

        if (fedRes && fedRes.ok) {
          const feds = await fedRes.json();
          if (feds && feds.length > 0) {
            setFederations(feds);
          }
        }
      } catch (error) {
        console.error('Failed to fetch footer content:', error);
      }
    };

    fetchContent();
  }, []);

  // Helper to get text in current language
  const getText = (obj, fallback = '') => {
    if (!obj) return fallback;
    return obj[language] || obj.ca || obj.es || fallback;
  };

  const socialLinks = [
    {
      name: 'Instagram',
      url: siteConfig.instagram_url,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      )
    },
    {
      name: 'Facebook',
      url: siteConfig.facebook_url,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      )
    },
    {
      name: 'LinkedIn',
      url: siteConfig.linkedin_url,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      )
    }
  ];

  return (
    <footer className="bg-[#F9F9F7] border-t border-[#E5E5E5]" data-testid="footer">
      {/* Main footer content */}
      <div className="container-custom py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">
          {/* Column 1: Logo & Contact - Logo com element principal */}
          <div className="space-y-8">
            <div className="flex flex-col items-center text-center space-y-5">
              {/* Logo institucional principal */}
              <img 
                src="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/ibb3s625_UGOLF%20ARAVELL%20-%20ANDORRA%20-%20BLANCO.svg"
                alt="UGOLF Aravell Andorra"
                className="h-16 lg:h-[4.5rem] w-auto brightness-0"
              />
              
              {/* LeClub - segell de xarxa (secundari) */}
              <a 
                href={footerContent.leclub_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1.5 group"
                data-testid="footer-leclub"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/iiexaz7w_Leclubverd.svg"
                  alt="LeClub Golf"
                  className="h-10 w-auto opacity-70 group-hover:opacity-100 transition-opacity duration-200"
                />
                <span className="text-[11px] text-[#888888] group-hover:text-[#1A4D2E] transition-colors duration-200">
                  {getText(footerContent.leclub_text, 'Membre de la xarxa LeClub Golf')}
                </span>
              </a>
            </div>
            
            {/* Dades de contacte - secundàries */}
            <div className="space-y-3 pt-2">
              <a 
                href={`tel:${siteConfig.phone?.replace(/\s/g, '')}`}
                className="flex items-center gap-3 text-[#666666] hover:text-[#1A4D2E] transition-colors duration-200"
                data-testid="footer-phone"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm">{siteConfig.phone}</span>
              </a>
              <a 
                href={`mailto:${siteConfig.contact_email}`}
                className="flex items-center gap-3 text-[#666666] hover:text-[#1A4D2E] transition-colors duration-200"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm">{siteConfig.contact_email}</span>
              </a>
              <a 
                href={siteConfig.maps_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-[#666666] hover:text-[#1A4D2E] transition-colors duration-200"
                data-testid="footer-address"
              >
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{siteConfig.address}</span>
              </a>
              <a 
                href="https://customer-assets.emergentagent.com/job_56a2be9b-13bb-4192-ac28-715d5016befb/artifacts/e30roilr_Joan_Monzo_vcard_final_con_trabajo%20%281%29.vcf"
                download="Direccio_UGOLF_Aravell.vcf"
                className="flex items-center gap-3 text-[#666666] hover:text-[#1A4D2E] transition-colors duration-200"
                data-testid="footer-vcf"
              >
                <UserCircle className="w-4 h-4" />
                <span className="text-sm">Direcció VCF</span>
              </a>
            </div>
          </div>

          {/* Column 2: Navigation */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A4D2E]">
              {getText(footerContent.nav_title, 'Navegació')}
            </h4>
            <nav className="grid grid-cols-2 gap-2">
              <Link to="/el-camp" className="footer-link">{t('nav.elCamp')}</Link>
              <Link to="/jugar" className="footer-link">{t('nav.jugar')}</Link>
              <Link to="/competicio" className="footer-link">{t('nav.competicio')}</Link>
              <Link to="/iniciacio" className="footer-link">{t('nav.iniciacio')}</Link>
              <Link to="/restaurant" className="footer-link">{t('nav.restaurant')}</Link>
              <Link to="/contacte" className="footer-link">{t('nav.contacte')}</Link>
            </nav>
          </div>

          {/* Column 3: Legal */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A4D2E]">
              {getText(footerContent.legal_title, 'Legal')}
            </h4>
            <nav className="flex flex-col gap-2">
              <Link to="/legal/avis-legal" className="footer-link" data-testid="footer-avis-legal">
                {t('footer.avisLegal')}
              </Link>
              <Link to="/legal/privacitat" className="footer-link" data-testid="footer-privacitat">
                {t('footer.politicaPrivacitat')}
              </Link>
              <Link to="/legal/cookies" className="footer-link" data-testid="footer-cookies">
                {t('footer.politicaCookies')}
              </Link>
              <button 
                className="footer-link text-left" 
                data-testid="footer-cookie-settings"
                onClick={() => window.openCookieSettings?.()}
              >
                {t('footer.configuracioCookes')}
              </button>
            </nav>
          </div>

          {/* Column 4: Social & Federations */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A4D2E]">
                {getText(footerContent.social_title, 'Segueix-nos')}
              </h4>
              <div className="flex gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label={social.name}
                    data-testid={`footer-social-${social.name.toLowerCase()}`}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A4D2E]">
                {getText(footerContent.federations_title, 'Federacions')}
              </h4>
              <div className="flex flex-wrap gap-2">
                {federations.map((fed) => (
                  <a
                    key={fed.id || fed.abbr}
                    href={fed.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 text-xs font-medium text-[#666666] bg-white border border-[#E5E5E5] hover:border-[#1A4D2E] hover:text-[#1A4D2E] transition-colors duration-200"
                    title={fed.name}
                    data-testid={`footer-fed-${fed.abbr?.toLowerCase()}`}
                  >
                    {fed.abbr}
                  </a>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A4D2E]">
                {getText(footerContent.communities_title, 'Comunitats')}
              </h4>
              <div className="flex flex-col gap-2">
                {defaultCommunities.map((comm) => (
                  <Link
                    key={comm.id}
                    to={comm.url}
                    className="text-sm text-[#666666] hover:text-[#1A4D2E] transition-colors duration-200"
                    data-testid={`footer-comm-${comm.id}`}
                  >
                    {comm.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[#E5E5E5]">
        <div className="container-custom py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <p className="text-sm text-[#666666]">
              © {currentYear} UGOLF ARAVELL Andorra. {t('footer.drets')}
            </p>
            <a
              href="/admin"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#AAAAAA] hover:text-[#888888] transition-colors duration-200"
              data-testid="footer-admin-link"
            >
              Accés administració
            </a>
          </div>
          <a
            href={siteConfig.booking_url || 'https://open.teeone.golf/es/aravell'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold tracking-[0.15em] uppercase text-[#1A4D2E] hover:text-[#D4AF37] transition-colors duration-200"
            data-testid="footer-reserva"
          >
            {t('nav.reserva')}
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
